// The staff Interval Training draws a whole pattern on (issue #3721), from the "Interval Training:
// listen", "sing it back" and "pattern result" artboards: where each note's column is, which step
// each note sits on, how tall the staff is for a session, and where the singer's dot and a missed
// note's landing go. Pure numbers - no React, no DOM, no audio - like Pitch Match's
// staff_geometry.ts, whose steps, stems, ledger lines and line spacing this reuses.
//
// A note's step is the home note's step plus its degree: a degree counts scale steps from do, and a
// scale step is one line or space, so do-re-mi climbs line, space, line from wherever do sits.
//
// The band. Pitch Match draws its clef's whole twenty-step band, which is taller than a screen that
// also carries a pattern's syllables, its result and its buttons can afford. This staff is sized to
// the session instead: from bandMarginSteps below the lower of the bottom line and the lowest note
// any pattern of the session sings, to bandMarginSteps above the higher of the top line and the
// highest. It is worked out from the home note and the session's lowest and highest degree, not from
// the pattern on screen, so the staff never changes height between patterns. The singer's dot and a
// missed note's landing are clamped to it, and the SVG keeps a halo's room around it so neither is
// cut off at the band's edge.
//
// Columns. One per note of the pattern, spread evenly across the staff lines: the note at index i of
// n sits at the middle of the i-th of n equal slices of the lines' length, so three notes stand at
// about 1/6, 3/6 and 5/6 of the way across, as the artboards draw them. The singer's dot starts on
// the column of the note being sung and slides toward the next one as the hold fills (intervalDotX).

import { IntervalPattern } from '../../../../../common/src/interval_training.ts';
import { Clef } from '../../../../../common/src/learn_rules.ts';
import { VocalRange } from '../../../../../common/src/model.ts';
import { staffLayout, staffStepForMidi } from '../pitch/staff_geometry.ts';

// ---- Steps -----------------------------------------------------------------------------------

// The step a degree sits on once do has been chosen: 0 is the bottom line, as in Pitch Match
export function intervalNoteStep(homeMidi: number, degree: number, clef: Clef): number {
  return staffStepForMidi(homeMidi, clef) + degree;
}

// The lowest and highest degree any of the session's patterns sings: what the band is built from
export function intervalDegreeSpan(patterns: IntervalPattern[]): { lowDegree: number; highDegree: number } {
  const degrees = patterns.flatMap((pattern) => pattern.degrees);
  return { lowDegree: Math.min(...degrees), highDegree: Math.max(...degrees) };
}

// ---- The band --------------------------------------------------------------------------------

export interface IntervalStaffBand {
  minStep: number;
  maxStep: number;
}

const topLineStep = 8;
const bottomLineStep = 0;

// Steps of room beyond the staff lines or the outermost note, so a stem and the singer's dot near a
// note have somewhere to be
export const bandMarginSteps = 2;

export function intervalStaffBand({
  homeMidi,
  clef,
  lowDegree,
  highDegree,
}: {
  homeMidi: number;
  clef: Clef;
  lowDegree: number;
  highDegree: number;
}): IntervalStaffBand {
  const lowest = Math.min(bottomLineStep, intervalNoteStep(homeMidi, lowDegree, clef));
  const highest = Math.max(topLineStep, intervalNoteStep(homeMidi, highDegree, clef));
  return { minStep: lowest - bandMarginSteps, maxStep: highest + bandMarginSteps };
}

// The notes that sit on or near the clef's five lines: from the first ledger line below the staff to
// the first ledger line above it, as midi numbers, both naturals. The page picks the session's do so
// that do and every note the session sings fall in here, where it can (it is the `within` limit
// pickHomeNote takes besides the singer's range): then the band stays a few steps either side of
// the staff and the screen fits a phone, rather than growing for a tenor whose do sits far above
// the bass staff.
export function nearStaffMidiRange(clef: Clef): VocalRange {
  const lowestStep = bottomLineStep - 2;
  const highestStep = topLineStep + 2;
  const midis = Array.from({ length: 128 }, (_midi, midi) => midi);
  // A note that is not a natural shares the step of the natural below it, so the highest note on a
  // step is the natural only when the note above it moves to the next step
  const isNatural = (midi: number) => staffStepForMidi(midi - 1, clef) < staffStepForMidi(midi, clef);
  const inside = midis.filter((midi) => {
    const step = staffStepForMidi(midi, clef);
    return step >= lowestStep && step <= highestStep && isNatural(midi);
  });
  return { lowMidi: inside[0], highMidi: inside[inside.length - 1] };
}

export function clampToBand(step: number, band: IntervalStaffBand): number {
  return Math.max(band.minStep, Math.min(band.maxStep, step));
}

// Where the singer's dot sits, or where a missed or close note landed, as a continuous step: 100
// cents from the note is one step, as Pitch Match's dotStep has it, clamped to the session's band
export function intervalDotStep(noteStep: number, cents: number, band: IntervalStaffBand): number {
  return clampToBand(noteStep + cents / 100, band);
}

// ---- Layout ----------------------------------------------------------------------------------

const dotRadius = 12; // the artboards' dot: smaller than Pitch Match's, five of them share the width
const haloRadius = 17;
// Above and below the band, room for a halo on a dot clamped to its edge
const bandPadding = haloRadius + 3;

const normalNotehead = { rx: 10.5, ry: 7.75 };
const emphasizedNotehead = { rx: staffLayout.noteheadRx, ry: staffLayout.noteheadRy }; // Pitch Match's

export const intervalStaffLayout = {
  width: staffLayout.width,
  lineX1: staffLayout.lineX1,
  lineX2: staffLayout.lineX2,
  lineWidth: staffLayout.lineWidth,
  stepHeight: staffLayout.stepHeight, // Pitch Match's line spacing, unchanged
  bandPadding,
  // A sounding note, or the one being sung, is drawn at Pitch Match's notehead size; the rest a
  // little smaller, as the artboards draw them
  normalNotehead,
  emphasizedNotehead,
  noteheadRotation: staffLayout.noteheadRotation,
  stemInsetX: 1.5, // the stem stands this far inside the notehead's right (or left) edge
  stemFootOffsetY: staffLayout.stemFootOffsetY,
  stemLength: 52, // shorter than Pitch Match's, so a pattern's stems stay inside a session's band
  stemWidth: staffLayout.stemWidth,
  ledgerHalfWidth: 19, // a short ledger line centred on its own notehead, clear of its neighbours'
  dotRadius,
  dotStrokeWidth: staffLayout.dotStrokeWidth,
  haloRadius,
  haloStrokeWidth: staffLayout.haloStrokeWidth,
  haloOpacity: staffLayout.haloOpacity,
};

// The SVG's height for a band: the band's steps with a halo's room above and below
export function intervalStaffHeight(band: IntervalStaffBand): number {
  return bandPadding * 2 + (band.maxStep - band.minStep) * staffLayout.stepHeight;
}

// Where a step sits inside the SVG: the band's top step at the top padding, y growing downwards
export function intervalStepY(step: number, band: IntervalStaffBand): number {
  return bandPadding + (band.maxStep - step) * staffLayout.stepHeight;
}

// The centre of note `index`'s column, for a pattern of `count` notes (see the header)
export function intervalColumnX(index: number, count: number): number {
  const span = staffLayout.lineX2 - staffLayout.lineX1;
  return staffLayout.lineX1 + (span * (2 * index + 1)) / (2 * count);
}

// How wide each of `count` columns is: one slice of the lines' length, which is also the distance
// between neighbouring columns' centres. A note's tap area is its whole column, so the columns'
// areas meet edge to edge and cover the lines from end to end.
export function intervalColumnWidth(count: number): number {
  return (staffLayout.lineX2 - staffLayout.lineX1) / count;
}

// Where the singer's dot sits across the staff (a trial, issue #3721): it slides right from the
// column of note `index` as the hold fills, by `held` - the hold's fraction, clamped to 0..1 - of
// one column spacing, so it reaches the next note's column exactly as the note locks. The last
// note has no next column, so its dot slides the same distance and stops where its halo still
// stays inside the staff lines' length (or on the last column, should that ever be further right).
export function intervalDotX(index: number, count: number, held: number): number {
  const fraction = Math.max(0, Math.min(1, held));
  const spacing = intervalColumnWidth(count);
  const rightmost = Math.max(intervalColumnX(count - 1, count), staffLayout.lineX2 - haloRadius);
  return Math.min(intervalColumnX(index, count) + fraction * spacing, rightmost);
}

// The syllables under the staff share the columns: the lines' inset from the SVG's edges, as a
// fraction of its width, so an HTML row padded by it puts each syllable under its note
export const intervalColumnInset = staffLayout.lineX1 / staffLayout.width;
